import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useUiMessages } from "./UiMessages";

export interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showInfo?: boolean;
}

type PaginationItem = number | "start-ellipsis" | "end-ellipsis";

export const getPaginationItems = (page: number, totalPages: number): PaginationItem[] => {
  if (totalPages <= 0) return [];
  const current = Math.min(Math.max(page, 1), totalPages);
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "end-ellipsis", totalPages];
  if (current >= totalPages - 3) return [1, "start-ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "start-ellipsis", current - 1, current, current + 1, "end-ellipsis", totalPages];
};

export const Pagination: React.FC<PaginationProps> = ({
  page, limit, total, totalPages, onPageChange, showInfo = true,
}) => {
  const { pagination } = useUiMessages();
  if (totalPages <= 1) return null;
  const current = Math.min(Math.max(page, 1), totalPages);
  const startItem = (current - 1) * limit + 1;
  const endItem = Math.min(current * limit, total);
  const items = getPaginationItems(current, totalPages);

  const btnBase = "relative inline-flex min-h-10 min-w-10 items-center justify-center border px-2 py-2 text-sm font-medium transition-colors cursor-pointer";
  const btnNormal = `${btnBase} border-border-default bg-bg-surface text-text-muted hover:bg-bg-elevated disabled:opacity-50 disabled:cursor-not-allowed`;

  return (
    <div className="flex items-center justify-between border-t border-border-subtle bg-bg-surface px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button type="button" onClick={() => onPageChange(current - 1)} disabled={current <= 1} className={`${btnNormal} rounded-md px-4`}>
          {pagination.previousPage}
        </button>
        <button type="button" onClick={() => onPageChange(current + 1)} disabled={current >= totalPages} className={`${btnNormal} ml-3 rounded-md px-4`}>
          {pagination.nextPage}
        </button>
      </div>
      <div className={`hidden sm:flex sm:flex-1 sm:items-center ${showInfo ? "sm:justify-between" : "sm:justify-end"}`}>
        {showInfo && (
          <p className="text-sm text-text-secondary">
            {pagination.pageInfo(startItem, endItem, total)}
          </p>
        )}
        <nav aria-label={pagination.label} className="relative z-0 -space-x-px inline-flex rounded-md shadow-sm">
          <button
            type="button"
            aria-label={pagination.previousPage}
            onClick={() => onPageChange(current - 1)}
            disabled={current <= 1}
            className={`${btnNormal} rounded-l-md`}
          >
            <ChevronLeft aria-hidden className="h-5 w-5" />
          </button>
          {items.map((item) =>
            typeof item === "number" ? (
              <button
                key={item}
                type="button"
                aria-label={pagination.pageLabel(item)}
                aria-current={item === current ? "page" : undefined}
                onClick={() => onPageChange(item)}
                className={`${btnBase} ${item === current ? "z-10 border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400" : "border-border-default bg-bg-surface text-text-muted hover:bg-bg-elevated"}`}
              >
                {item}
              </button>
            ) : (
              <span key={item} aria-hidden className="relative inline-flex min-h-10 min-w-10 items-center justify-center border border-border-default bg-bg-surface px-2 py-2 text-sm text-text-muted">
                …
              </span>
            ),
          )}
          <button
            type="button"
            aria-label={pagination.nextPage}
            onClick={() => onPageChange(current + 1)}
            disabled={current >= totalPages}
            className={`${btnNormal} rounded-r-md`}
          >
            <ChevronRight aria-hidden className="h-5 w-5" />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
